const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

// Helper function to decode JWT token
const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('[authService] Error decoding token:', error);
    return null;
  }
};

const authService = {
  // Signup
  signup: async (email, password, fullName, referralToken) => {
    try {
      const body = {
        email,
        password,
        fullName,
        referralToken: referralToken,
      };

      const response = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Signup failed');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  // ==================== GOOGLE OAUTH ====================

  googleLogin: async (googleToken) => {
    try {
      console.log('[authService] Sending Google token to backend...');

      const response = await fetch(`${API_BASE_URL}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: googleToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.log('[authService] Google login error:', data);
        throw new Error(data.message || 'Google login failed');
      }

      console.log('[authService] Google login successful!', data);

      // Store tokens and decode JWT to extract role and companyId
      if (data.accessToken) {
        authService.storeAuthData(data);
      }

      return data;
    } catch (error) {
      console.error('[authService] Error:', error);
      throw new Error(error.message || 'Google login failed');
    }
  },

  // Login
  login: async (email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      // Store tokens and decode JWT to extract role and companyId
      authService.storeAuthData(data);

      return data;
    } catch (error) {
      throw error;
    }
  },

  // ==================== NEW: Store auth data with JWT decoding ====================
  storeAuthData: (data) => {
    // Store tokens
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    localStorage.setItem('user', JSON.stringify(data.user));

    // Decode JWT to extract role and companyId
    const decodedToken = decodeToken(data.accessToken);

    if (decodedToken) {
      console.log('[authService] Decoded JWT:', decodedToken);

      // Store role and companyId separately for easy access
      if (decodedToken.role) {
        localStorage.setItem('userRole', decodedToken.role);
        console.log('[authService] User role:', decodedToken.role);
      }

      if (decodedToken.companyId) {
        localStorage.setItem('companyId', decodedToken.companyId.toString());
        console.log('[authService] Company ID:', decodedToken.companyId);
      }
    }
  },

  // Logout
  logout: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
    localStorage.removeItem('companyId');
  },

  // Get current user
  getCurrentUser: async () => {
    try {
      const token = localStorage.getItem('accessToken');

      if (!token) {
        throw new Error('No token found');
      }

      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to get user');
      }

      return data.user;
    } catch (error) {
      throw error;
    }
  },

  // Verify email
  verifyEmail: async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Email verification failed');
      }

      return data;
    } catch (error) {
      throw new Error(error.message || 'Email verification failed');
    }
  },

  // Get stored token
  getToken: () => {
    return localStorage.getItem('accessToken');
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    return !!localStorage.getItem('accessToken');
  },

  // Get stored user data
  getStoredUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  // ==================== NEW: Role and Company Helper Functions ====================

  // Get user role (USER, ADMIN, COMPANY_ADMIN)
  getRole: () => {
    return localStorage.getItem('userRole');
  },

  // Get company ID
  getCompanyId: () => {
    const companyId = localStorage.getItem('companyId');
    return companyId ? parseInt(companyId) : null;
  },

  // Check if user is admin (either ADMIN or COMPANY_ADMIN)
  isAdmin: () => {
    const role = localStorage.getItem('userRole');
    return role === 'ADMIN' || role === 'COMPANY_ADMIN';
  },

  // Check if user is regular user
  isUser: () => {
    const role = localStorage.getItem('userRole');
    return role === 'USER';
  },

  // Get full user context (for debugging/display)
  getUserContext: () => {
    return {
      user: authService.getStoredUser(),
      role: authService.getRole(),
      companyId: authService.getCompanyId(),
      isAdmin: authService.isAdmin(),
      isAuthenticated: authService.isAuthenticated(),
    };
  },

  // ==================== REFERRAL SYSTEM ====================

  // Validate referral token (called by signup page)
  validateReferralToken: async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/referrals/validate/${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Invalid referral token');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  googleSignupWithReferral: async (googleToken, referralToken) => {
    try {
      console.log('[authService] Google signup with referral token');

      const response = await fetch(`${API_BASE_URL}/auth/google-signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          googleToken: googleToken,
          referralToken: referralToken,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Google signup failed');
      }

      // Store auth data with role/company extraction
      if (data.accessToken) {
        authService.storeAuthData(data);
      }

      return data;
    } catch (error) {
      console.error('[authService] Google signup error:', error);
      throw error;
    }
  },

  // Send referral invitation
  sendReferralInvitation: async (invitedEmail) => {
    try {
      const token = localStorage.getItem('accessToken');

      const response = await fetch(`${API_BASE_URL}/referrals/send-invitation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ invitedEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send invitation');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  // Get referral statistics
  getReferralStats: async () => {
    try {
      const token = localStorage.getItem('accessToken');

      const response = await fetch(`${API_BASE_URL}/referrals/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to get referral stats');
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

};

export default authService;